import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

import { Controls } from '../../components/controls/Controls';
import useToast from '../../hooks/useToast';
import { useApiClient, convertAxiosErrorToUserFacingMessage } from '../../context/ApiClientContext';
import basicServiceHandler from '../../services/basicServiceHandler';

const QUESTION_TYPES_ALLOWING_IMAGE = ['selectOne', 'selectAll'];

export default function ImageUploadButton(props) {
  const {
    questionId,
    questionType,
    imgUrl,
    refreshValues,
  } = props;

  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);
  const apiClient = useApiClient();

  const {
    toastSetSuccessNotification,
    toastSetErrorNotification,
  } = useToast();

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImageLoading(true);
    setImageError(false);
    basicServiceHandler(
      () => apiClient.uploadQuestionImage(questionId, file),
      () => {
        toastSetSuccessNotification('Image uploaded successfully!');
        refreshValues();
      },
      (error) => toastSetErrorNotification(convertAxiosErrorToUserFacingMessage(error)),
      () => setImageLoading(false),
    );
  };

  const handleImageRemove = async () => {
    setImageLoading(true);
    basicServiceHandler(
      () => apiClient.removeQuestionImage(questionId),
      () => {
        toastSetSuccessNotification('Image removed successfully!');
        refreshValues();
      },
      (error) => toastSetErrorNotification(convertAxiosErrorToUserFacingMessage(error)),
      () => setImageLoading(false),
    );
  };

  const showImageUpload = QUESTION_TYPES_ALLOWING_IMAGE.includes(questionType);

  if (!showImageUpload) {
    return null;
  }

  if (imgUrl && !imageError) {
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            component="img"
            src={imgUrl}
            alt="Question image"
            onError={() => setImageError(true)}
            sx={{
              maxWidth: '200px',
              maxHeight: '150px',
              objectFit: 'contain',
              border: '1px solid #ccc',
              borderRadius: 1,
            }}
          />
          <IconButton
            onClick={handleImageRemove}
            disabled={imageLoading}
            color="error"
            title="Remove image"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {imageError && (
        <Box
          sx={{
            width: '200px',
            height: '150px',
            backgroundColor: '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            mb: 1,
          }}
        >
          <Typography variant="caption" color="error">
            Failed to load image
          </Typography>
        </Box>
      )}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />
      <Controls.Button
        variant="outlined"
        onClick={() => fileInputRef.current?.click()}
        loading={imageLoading}
        text="Upload Image"
        startIcon={<CloudUploadIcon />}
        size="medium"
        sx={{ margin: '6px' }}
      />
    </Box>
  );
}

ImageUploadButton.propTypes = {
  questionId: PropTypes.number.isRequired,
  questionType: PropTypes.string.isRequired,
  imgUrl: PropTypes.string,
  refreshValues: PropTypes.func.isRequired,
};

ImageUploadButton.defaultProps = {
  imgUrl: null,
};
